import { useCountdown, useDocumentVisibility } from '@vueuse/core';
import { differenceInSeconds } from 'date-fns';
import type { MaybeRef } from 'vue';
import { computed, unref, watch } from 'vue';

export interface CountdownTime {
    hours: number;
    minutes: number;
    seconds: number;
    totalSeconds: number;
    totalMilliseconds: number;
}

export interface CountdownOptions {
    interval?: number;
    onComplete?: () => void;
    autoStart?: boolean;
}

function formatNumber(num: number) {
    return num.toString().padStart(2, '0');
}

export function useCountdownTimer(
    targetTime: MaybeRef<Date | number>,
    options: CountdownOptions = {},
) {
    const {
        interval = 1000,
        onComplete,
        autoStart = true,
    } = options;

    const getInitialSeconds = () => {
        const target = unref(targetTime);
        const targetDate = target instanceof Date ? target : new Date(target);
        return Math.max(0, differenceInSeconds(targetDate, new Date()));
    };

    const { remaining, start, stop, pause, resume } = useCountdown(getInitialSeconds(), {
        interval,
        onComplete,
        immediate: autoStart,
    });

    const reset = () => {
        stop();
        start(getInitialSeconds());
    };

    const visibility = useDocumentVisibility();
    watch(visibility, (isVisible) => {
        if (isVisible === 'visible') {
            reset();
        }
    });

    watch(() => unref(targetTime), () => {
        const newSeconds = getInitialSeconds();
        stop();
        start(newSeconds);
    });

    const timeDifference = computed((): CountdownTime => {
        const totalSeconds = remaining.value;
        const totalMilliseconds = totalSeconds * 1000;
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return {
            hours,
            minutes,
            seconds,
            totalSeconds,
            totalMilliseconds,
        };
    });

    const isTimeUp = computed(() => remaining.value === 0);

    const startTimer = () => start(getInitialSeconds());

    const formattedTime = computed(() => {
        const { hours, minutes, seconds } = timeDifference.value;
        return {
            hours: formatNumber(hours),
            minutes: formatNumber(minutes),
            seconds: formatNumber(seconds),
        };
    });

    return {
        timeDifference,
        formattedTime,
        isTimeUp,
        start: startTimer,
        stop,
        pause,
        resume,
        reset,
    };
}
